import { useEffect, useState, useCallback, useMemo } from 'react';
import { TABS } from '@constants/common.constants';

import { DateTime } from 'luxon';
import QueryTransaction from '@interfaces/transaction';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, AppState } from '@/state';
import { FILTER_PERIOD_TIME } from '@constants/date-time.constants';
import { fetchTransactions, fetchMoreTransactions, setRefreshing as setTransactionsRefreshing } from '@/state/slice/transactionsSlice';
import { useAutoFetchPagination } from '@hooks/useAutoFetchPagination';
import { fetchRedemptions, setRefreshing as setRedemptionsRefreshing } from '@/state/slice/redemptionsSlice';
import { QUERY_PARAMS } from '@constants/route.constant';
import { useLocation, useNavigate } from 'react-router-dom';
import { NotificationPermission } from '@constants/notification.constants';
import { useOutletContext } from 'react-router-dom';
import { ITransactions, ITransaction } from '@interfaces/transaction';
import { IRedemption } from '@interfaces/redemption';
import { BodyLayoutContext } from '@interfaces/common.interface';
import useMaintenanceCheck from '@hooks/useMaintenanceCheck';
import PullToRefresh from '@components/PullToRefresh';
import HomeTabContent from '@pages/Home/components/HomeTabContent';
import Info from '@assets/images/info.svg';
import Tooltip from '@components/Tooltip';

const tabs = [TABS.PAYMENTS, TABS.REDEMPTIONS];

const checkNewData = <T extends ITransaction | IRedemption>(
	data: ITransactions<T>[],
	setHasNewData: React.Dispatch<React.SetStateAction<boolean>>
) => {
	const hasNewData = data?.some((day) => day.transactions.some((txn: any) => txn.isNew));
	setHasNewData(hasNewData);
};

export default function Home() {
	const dispatch = useDispatch<AppDispatch>();
	const transactionsData = useSelector((state: AppState) => state.transactionsState);
	const redemptionData = useSelector((state: AppState) => state['redemptionState']);

	const navigate = useNavigate();
	const location = useLocation();
	const params = new URLSearchParams(location.search);
	const [newTransaction, setNewTransaction] = useState(false);
	const [newRedemption, setNewRedemption] = useState(false);
	const { setTriggerNotificationOffAlert } = useOutletContext<BodyLayoutContext>();
	useMaintenanceCheck();

	// Memoize query to prevent unnecessary re-renders
	const query = useMemo(() => ({
		sort: 'createdAt',
		start: DateTime.now().minus({ days: FILTER_PERIOD_TIME.RECENT_TXN }).startOf('day').toJSDate(),
	} as QueryTransaction), []);

	const getTransactions = useCallback(async () => {
		try {
			dispatch(fetchTransactions(query));
		} catch (error) {
			console.error('Error fetching transaction:', error);
		}
	}, [dispatch, query]);

	const getRedemptions = useCallback(async () => {
		try {
			dispatch(fetchRedemptions({ ...query, page: 1 }));
		} catch (error) {
			console.error('Error fetching redemptions:', error);
		}
	}, [dispatch, query]);

	const [selectedTab, setSelectedTab] = useState(params.get(QUERY_PARAMS.TAB) ?? TABS.PAYMENTS);

	const onRefresh = useCallback((): Promise<void> => {
		dispatch(setTransactionsRefreshing(true));
		dispatch(setRedemptionsRefreshing(true));
		if (selectedTab === TABS.REDEMPTIONS) {
			return getRedemptions();
		}
		return getTransactions();
	}, [dispatch, selectedTab, getRedemptions, getTransactions]);

	const checkNotificationPermission = useCallback((hasNewData: boolean) => {
		if (typeof Notification === 'undefined') return console.error('No Notification API');
		if (hasNewData && (Notification.permission === NotificationPermission.DENIED || Notification.permission === NotificationPermission.DEFAULT)) {
			setTriggerNotificationOffAlert(true);
		}
	}, [setTriggerNotificationOffAlert]);

	useEffect(() => {
		if (selectedTab === TABS.REDEMPTIONS) {
			void getRedemptions();
		} else {
			void getTransactions();
		}
	}, [selectedTab]);

	useEffect(() => {
		checkNewData(transactionsData.transactions, setNewTransaction);
	}, [transactionsData]);

	useEffect(() => {
		checkNewData(redemptionData.transactions, setNewRedemption);
	}, [redemptionData]);

	// Auto-fetch more transactions when hasMore is true
	useAutoFetchPagination({
		hasMore: transactionsData.hasMore,
		nextCursor: transactionsData.nextCursor,
		isLoadingMore: transactionsData.isLoadingMore,
		baseQuery: query,
		fetchMoreAction: fetchMoreTransactions,
	});

	useEffect(() => {
		checkNotificationPermission(newTransaction);
	}, [newTransaction]);

	useEffect(() => {
		checkNotificationPermission(newRedemption);
	}, [newRedemption]);

	const onChangeTab = useCallback((tab: string) => {
		setSelectedTab(tab);
		if (tab === TABS.PAYMENTS) {
			setNewTransaction(false);
		}
		if (tab === TABS.REDEMPTIONS) {
			setNewRedemption(false);
		}
		navigate(`/?${QUERY_PARAMS.TAB}=${tab}`, { replace: true });
	}, [navigate]);

	return (
		<div className='flex flex-col items-center'>
			<div className='flex gap-11 desktop:gap-24 pt-6 desktop:pt-8 z-10'>
				{tabs.map((tab) => (
					<div className='flex flex-row items-center' key={tab}>
						<div
							className={`uppercase cursor-pointer pb-2 font-semibold leading-[17.05px] ${
								selectedTab === tab ? 'text-white border-b-[5px] border-b-cyan' : 'text-grey-light'
							}`}
							onClick={() => onChangeTab(tab)}>
							{tab}
						</div>
						{tab === TABS.REDEMPTIONS && (
							<div className={`ml-1 ${selectedTab === tab ? 'pb-3' : 'pb-2'}`}>
								<Tooltip
									className='left-[-273px] w-[327px] after:right-[34px] top-[calc(100%+12px)]'
									displayText={
										<div className='absolute -top-4 w-6 h-6 cursor-pointer'>
											<Info data-testid='tooltip-info-icon' />
										</div>
									}>
									<>
										Deals are pre-paid by customer. Apply voucher to customer's bill and collect any remaining balance, if applicable.
										<br />
										<br />
										Privileges do not need to be pre-purchased. Simply apply the privilege as per the terms & conditions and collect any
										payment, if applicable.
									</>
								</Tooltip>
							</div>
						)}
					</div>
				))}
			</div>

			<PullToRefresh key={selectedTab} onRefresh={onRefresh}>
				<HomeTabContent selectedTab={selectedTab} transactionsData={transactionsData} redemptionData={redemptionData} />
			</PullToRefresh>
		</div>
	);
}
